import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { IS_MOBILE_BUILD } from "../../config/buildProfile";
import {
  ESSENTIAL_VISITOR_STORAGE_KEY,
  getOrCreateEssentialVisitorId,
} from "../../utils/essentialAnalytics";
import { COOKIE_CONSENT_DECISION_KEY } from "../../utils/optionalCookies";

export const COOKIE_CONSENT_VISITOR_KEY = ESSENTIAL_VISITOR_STORAGE_KEY;
export { COOKIE_CONSENT_DECISION_KEY };
const COOKIE_CONSENT_FORCE_VISIBLE = false;

function getStoredDecision() {
  try {
    const stored = String(localStorage.getItem(COOKIE_CONSENT_DECISION_KEY) || "").trim();
    return stored === "accepted" || stored === "rejected" ? stored : null;
  } catch {
    return null;
  }
}

export default function CookieConsentBanner({ isDesktop = false }) {
  const location = useLocation();
  const { t } = useTranslation("auth");
  const consentDisabled = IS_MOBILE_BUILD;
  const visitorIdRef = useRef("");
  const [decision, setDecision] = useState(() => getStoredDecision());
  const [loaded, setLoaded] = useState(() => Boolean(getStoredDecision()));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const sourcePath = useMemo(
    () => `${location.pathname || "/"}${location.search || ""}`,
    [location.pathname, location.search]
  );

  const ensureVisitorId = () => {
    const current = String(visitorIdRef.current || "").trim();
    if (current) return current;
    const next = String(getOrCreateEssentialVisitorId() || "").trim();
    visitorIdRef.current = next;
    return next;
  };

  useEffect(() => {
    if (consentDisabled) return;
    visitorIdRef.current = getOrCreateEssentialVisitorId();
  }, [consentDisabled]);

  useEffect(() => {
    if (consentDisabled) {
      setLoaded(true);
      return undefined;
    }

    let cancelled = false;

    const fetchConsent = async () => {
      const storedDecision = getStoredDecision();
      if (storedDecision) {
        setDecision(storedDecision);
        setLoaded(true);
        return;
      }

      try {
        const response = await api.get("/accounts/cookie-consent/", {
          params: { visitor_id: ensureVisitorId() },
          meta: {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
            suppressGlobalError: true,
          },
        });
        if (cancelled) return;
        const serverDecision = response?.data?.cookie_consent?.decision || null;
        if (serverDecision === "accepted" || serverDecision === "rejected") {
          try {
            localStorage.setItem(COOKIE_CONSENT_DECISION_KEY, serverDecision);
            window.dispatchEvent(
              new CustomEvent("vg-cookie-consent-updated", {
                detail: response?.data?.cookie_consent || { decision: serverDecision },
              })
            );
          } catch {
            // ignore
          }
        }
        setDecision(serverDecision);
      } catch {
        if (!cancelled) setDecision(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchConsent();
    return () => {
      cancelled = true;
    };
  }, [consentDisabled]);

  const handleSubmit = async (nextDecision) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError("");

    try {
      const response = await api.post(
        "/accounts/cookie-consent/",
        {
          decision: nextDecision,
          visitor_id: ensureVisitorId(),
          source_path: sourcePath,
          banner_version: "v1",
        },
        {
          meta: {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
            suppressGlobalError: true,
          },
        }
      );

      const savedDecision = response?.data?.cookie_consent?.decision || nextDecision;
      setDecision(savedDecision);

      try {
        localStorage.setItem(COOKIE_CONSENT_DECISION_KEY, savedDecision);
        window.dispatchEvent(
          new CustomEvent("vg-cookie-consent-updated", {
            detail: response?.data?.cookie_consent || { decision: savedDecision },
          })
        );
      } catch {
        // ignore
      }
    } catch {
      const fallbackDecision = nextDecision === "accepted" ? "accepted" : "rejected";
      setDecision(fallbackDecision);
      try {
        localStorage.setItem(COOKIE_CONSENT_DECISION_KEY, fallbackDecision);
        window.dispatchEvent(
          new CustomEvent("vg-cookie-consent-updated", {
            detail: { decision: fallbackDecision, accepted: fallbackDecision === "accepted" },
          })
        );
      } catch {
        // ignore
      }
      setError("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldRender = !consentDisabled && (COOKIE_CONSENT_FORCE_VISIBLE || (loaded && !decision));
  if (!shouldRender) return null;

  return (
    <section
      aria-label={t("cookie_banner.aria_label")}
      className={clsx(
        "pointer-events-auto w-full",
        isDesktop ? "max-w-[640px]" : "max-w-none"
      )}
    >
      <div
        className={clsx(
          "w-full text-[#A9CCF9]",
          "rounded-[28px] border border-[rgba(116,169,236,0.34)] bg-[rgba(3,3,6,0.78)]",
          isDesktop
            ? "px-6 py-5 shadow-[0_20px_52px_rgba(0,0,0,0.30)]"
            : "px-4 py-4 shadow-[0_18px_44px_rgba(0,0,0,0.28)]"
        )}
      >
        <div className={clsx("mx-auto flex flex-col", isDesktop ? "items-start text-left" : "items-center text-center")}>
          <h2
            className={clsx(
              "font-bold leading-none tracking-[-0.04em]",
              isDesktop ? "text-[18px]" : "text-[18px]"
            )}
          >
            {t("cookie_banner.title")}
          </h2>

          <p
            className={clsx(
              "font-medium leading-[1.3]",
              isDesktop ? "mt-2 text-[14px]" : "mt-2 text-[14px]"
            )}
          >
            {t("cookie_banner.subtitle")}
          </p>

          <p
            className={clsx(
              "font-normal leading-[1.45]",
              isDesktop ? "mt-3 max-w-none text-[13px]" : "mt-3 max-w-[92%] text-[12px]"
            )}
          >
            {t("cookie_banner.body")}
          </p>

          <div
            className={clsx(
              "flex w-full items-center",
              isDesktop ? "mt-4 justify-end gap-3" : "mt-4 justify-center gap-3"
            )}
          >
            <button
              type="button"
              onClick={() => handleSubmit("rejected")}
              disabled={isSubmitting}
              className={clsx(
                "inline-flex items-center justify-center rounded-full border border-[#6E88AE] font-semibold text-[#6E88AE] transition hover:border-[#9EC7FF] hover:text-[#9EC7FF] disabled:opacity-60",
                isDesktop
                  ? "h-11 min-w-[140px] px-5 text-[15px]"
                  : "h-10 flex-[0.9] px-4 text-[13px]"
              )}
              >
              {t("cookie_banner.reject")}
            </button>

            <button
              type="button"
              onClick={() => handleSubmit("accepted")}
              disabled={isSubmitting}
              className={clsx(
                "inline-flex items-center justify-center rounded-full bg-[#A9CCF9] font-semibold text-black transition hover:bg-[#bed9fb] disabled:opacity-60",
                isDesktop
                  ? "h-11 min-w-[160px] px-6 text-[15px]"
                  : "h-10 flex-[1.35] px-5 text-[13px]"
              )}
              >
              {t("cookie_banner.accept")}
            </button>
          </div>

          {error ? (
            <p className="mt-4 text-sm font-medium text-[#ff9c9c]">{error}</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
